import { 
  Radio, Target, Shield, Zap, 
  Swords, Package, TrendingUp, Crown, Crosshair
} from 'lucide-react';

export const MISSION_PROFILES = {
  simple: [
    {
      id: 's1',
      name: 'Supply Run',
      tier: 'simple',
      type: 'logistics',
      location: 'Market District',
      description: 'Routine supply procurement mission. Low risk, guaranteed returns.',
      duration: 2,
      requirements: { minSquad: 1, maxSquad: 2 },
      rewards: { supplies: 30, xp: 20 },
      risks: { casualty: 'Minimal', successRate: 95 },
      icon: Package,
      color: 'green'
    },
    {
      id: 's2',
      name: 'Patrol Route',
      tier: 'simple',
      type: 'patrol',
      location: 'Perimeter Zone',
      description: 'Standard patrol to maintain area security and gather local intel.',
      duration: 4,
      requirements: { minSquad: 2, maxSquad: 3 },
      rewards: { intel: 10, xp: 25 },
      risks: { casualty: 'Minimal', successRate: 90 },
      icon: Radio,
      color: 'green'
    },
    {
      id: 's3',
      name: 'Training Exercise',
      tier: 'simple',
      type: 'training',
      location: 'Base Training Grounds',
      description: 'Squad training to improve combat readiness and unit cohesion.',
      duration: 14,
      requirements: { minSquad: 2, maxSquad: 4 },
      rewards: { xp: 100 },
      risks: { casualty: 'None', successRate: 100 },
      icon: TrendingUp,
      color: 'green'
    }
  ],
  
  complex: [
    {
      id: 'c1',
      name: 'Warehouse Raid',
      tier: 'complex',
      type: 'raid',
      location: 'Industrial Sector',
      description: 'Tactical raid on enemy supply cache. Choose AUTO for quick resolution or MANUAL for direct control.',
      duration: 9,
      requirements: { minSquad: 3, maxSquad: 5 },
      rewards: { funds: 8000, supplies: 50, intel: 15, xp: 150 },
      risks: { casualty: 'Medium', successRate: 70 },
      threatLevel: 4,
      icon: Target,
      color: 'yellow'
    },
    {
      id: 'c2',
      name: 'HVT Assassination',
      tier: 'complex',
      type: 'elimination',
      location: 'Downtown Safehouse',
      description: 'Eliminate high-value target. Tactical operation requiring precision.',
      duration: 20,
      requirements: { minSquad: 2, maxSquad: 4 },
      rewards: { funds: 12000, intel: 30, xp: 200 },
      risks: { casualty: 'High', successRate: 60 },
      threatLevel: 6,
      icon: Crosshair,
      color: 'yellow'
    },
    {
      id: 'c3',
      name: 'Sabotage Mission',
      tier: 'complex',
      type: 'sabotage',
      location: 'Power Station',
      description: 'Infiltrate and disable enemy infrastructure. Stealth-focused operation.',
      duration: 16,
      requirements: { minSquad: 3, maxSquad: 4 },
      rewards: { funds: 10000, intel: 25, xp: 175 },
      risks: { casualty: 'Medium', successRate: 65 },
      threatLevel: 5,
      icon: Zap,
      color: 'yellow'
    },
    {
      id: 'c4',
      name: 'Hostage Rescue',
      tier: 'complex',
      type: 'rescue',
      location: 'Abandoned Factory',
      description: 'Extract civilians from hostile territory. Time-sensitive operation.',
      duration: 28,
      requirements: { minSquad: 4, maxSquad: 10 },
      rewards: { funds: 5000, intel: 10, xp: 120, reputation: 30 },
      risks: { casualty: 'Medium', successRate: 68 },
      threatLevel: 4,
      icon: Shield,
      color: 'yellow'
    }
  ],
  
  campaign: [
    {
      id: 'cm1',
      name: 'Operation Vanguard',
      tier: 'campaign',
      type: 'assault',
      location: 'Enemy Command Center',
      description: 'Major assault on enemy headquarters. Full squad deployment required. This is a pivotal campaign mission.',
      storyContext: 'Intelligence suggests the enemy commander is coordinating attacks from this facility. Eliminating them could turn the tide of the war.',
      requirements: { minSquad: 5, maxSquad: 6 },
      rewards: { funds: 25000, intel: 100, supplies: 75, xp: 500, reputation: 100 },
      risks: { casualty: 'Very High', successRate: 45 },
      threatLevel: 9,
      unlockDay: 15,
      icon: Crown,
      color: 'red'
    },
    {
      id: 'cm2',
      name: 'Liberation Day',
      tier: 'campaign',
      type: 'defense',
      location: 'City Center',
      description: 'Defend the city from a massive enemy offensive. The fate of the campaign hangs in the balance.',
      storyContext: 'The enemy has launched their final assault. Hold the line at all costs.',
      requirements: { minSquad: 6, maxSquad: 6 },
      rewards: { funds: 30000, intel: 150, xp: 750, reputation: 200 },
      risks: { casualty: 'Extreme', successRate: 35 },
      threatLevel: 10,
      unlockDay: 25,
      icon: Swords,
      color: 'red'
    }
  ]
};

// Helper function to deep clone missions while preserving icon components
const deepCloneMissions = (missions) => {
  const cloned = {
    simple: [],
    complex: [],
    campaign: []
  };
  
  ['simple', 'complex', 'campaign'].forEach(tier => {
    cloned[tier] = missions[tier].map(mission => {
      const icon = mission.icon; // Save the icon component
      const missionCopy = JSON.parse(JSON.stringify(mission)); // Clone everything else
      missionCopy.icon = icon; // Restore the icon component
      return missionCopy;
    });
  });
  
  return cloned;
};

// Helper function to get missions with theme customization
export const getMissions = (theme) => {
  const missions = deepCloneMissions(MISSION_PROFILES);
  
  if (theme?.missions) {
    // Apply theme customizations to mission names
    if (theme.missions.simpleNames) {
      missions.simple[0].name = theme.missions.simpleNames.supply || missions.simple[0].name;
      missions.simple[1].name = theme.missions.simpleNames.patrol || missions.simple[1].name;
      missions.simple[2].name = theme.missions.simpleNames.training || missions.simple[2].name;
    }
    
    if (theme.missions.complexNames) {
      missions.complex[0].name = theme.missions.complexNames.raid || missions.complex[0].name;
      missions.complex[1].name = theme.missions.complexNames.assassination || missions.complex[1].name;
      missions.complex[2].name = theme.missions.complexNames.sabotage || missions.complex[2].name;
      missions.complex[3].name = theme.missions.complexNames.rescue || missions.complex[3].name;
    }
    
    if (theme.missions.campaignNames) {
      missions.campaign[0].name = theme.missions.campaignNames.vanguard || missions.campaign[0].name;
      missions.campaign[1].name = theme.missions.campaignNames.liberation || missions.campaign[1].name;
    }
  }
  
  return missions;
};

// Helper function to check if a campaign mission is unlocked
export const isMissionUnlocked = (mission, currentCampaignDay = 0) => {
  if (mission.tier !== 'campaign') return true;
  return currentCampaignDay >= (mission.unlockDay || 0);
};

// Helper function to get all missions of a specific tier
export const getMissionsByTier = (tier, theme = null) => {
  const missions = getMissions(theme);
  return missions[tier] || [];
};

// Helper function to get a mission by ID
export const getMissionById = (missionId, theme = null) => {
  const missions = getMissions(theme);
  const allMissions = [...missions.simple, ...missions.complex, ...missions.campaign];
  return allMissions.find(m => m.id === missionId);
};